import {ReactElement, useEffect, useRef} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import * as css from "./css.module.scss";
import cn from "classnames";
import {Property} from "../../../../contexts/properties/PropertiesContext";
import {calculateTotalMonths, HOTData, PendingUpdates} from "./logic";
import {ReactComponent as MaximizeIcon} from '@zendeskgarden/svg-icons/src/16/maximize-stroke.svg';
import {ReactComponent as MinimizeIcon} from '@zendeskgarden/svg-icons/src/16/minimize-stroke.svg';
import {ReactComponent as ChevronRightIcon} from '@zendeskgarden/svg-icons/src/16/chevron-right-stroke.svg';
import {ReactComponent as ChevronDownIcon} from '@zendeskgarden/svg-icons/src/16/chevron-down-stroke.svg';
import {ContextMenu} from "handsontable/plugins";
import HotTable from "@handsontable/react";

export type ICellRenderer = {
    property: Property,
    rowNumToUnitTypeId: HOTData["rowNumToUnitTypeId"],
    actualOverridesByUnitTypeId: HOTData["actualOverridesByUnitTypeId"],
    calcOverridesByUnitTypeId: HOTData["calcOverridesByUnitTypeId"],
    invalidForecastedMonthsOverrideState: boolean[],
    pendingUpdates: PendingUpdates | undefined,
    isEditing: boolean,
    allRowsExpanded: boolean,
    hiddenRows: number[],
    hotRef: HotTable | null,
    handleRowToggleClick: (row: number) => void,
    handleAllRowsToggleClick: () => void;
    [key: string]: any,
};
export function CellRenderer(customProps: ICellRenderer, ..._: any): ReactElement {
    const {instance, TD, row, col, prop, value, cellProperties, property, rowNumToUnitTypeId,
        pendingUpdates, actualOverridesByUnitTypeId, calcOverridesByUnitTypeId, isEditing,
        allRowsExpanded, hiddenRows, handleRowToggleClick, handleAllRowsToggleClick,
        invalidForecastedMonthsOverrideState, hotRef
    } = customProps as ICellRenderer & {
        TD: HTMLTableCellElement;
    };
    const totalMonths = calculateTotalMonths(property);
    const unitTypeId = rowNumToUnitTypeId[row];
    function setupStyling() {
        const classNames = [];
        TD.className = "";
        if(!unitTypeId || col == 0) {
            cellProperties.readOnly = true;
        }
        if(col - 1 == 1) {
            classNames.push(css.forecastedSplit);
        }
        if(col - 1 == totalMonths - 12) {
            classNames.push(css.budgetSplit);
        }
        if(unitTypeId) {
            if(col - 1 == 0) {
                classNames.push(css.actualCell);
            }
            if(col - 1 >= 1) {
                classNames.push(css.forecastedCell);
            }
            const utPending = pendingUpdates?.unitTypeUpdates.get(unitTypeId);
            // hightlight modified cells
            const pendingValue = utPending?.[col - 1];
            if(pendingValue !== undefined) {
                classNames.push(css.modified);
                if(pendingValue !== null) {
                    classNames.push(css.overridenValue);
                }
            }
            // highlight overriden cells
            if(col - 1 == 0) {
                const overrideValue = actualOverridesByUnitTypeId[unitTypeId];
                if(overrideValue !== undefined && overrideValue !== null) {
                    classNames.push(css.overridenValue);
                    if(pendingUpdates?.unitTypeUpdates?.get(unitTypeId)?.[col - 1] === null) {
                        classNames.push(css.overridenValuePendingRemoval);
                    }
                }
            }
            if(col - 1 >= 1) {
                const overrideValue = calcOverridesByUnitTypeId[unitTypeId]?.[col - 1];
                if(overrideValue !== undefined && overrideValue !== null) {
                    classNames.push(css.overridenValue);
                    if(pendingUpdates?.unitTypeUpdates?.get(unitTypeId)?.[col - 1] === null) {
                        classNames.push(css.overridenValuePendingRemoval);
                    }
                }
            }
            if(col - 1 >= 0 && col - 1 < totalMonths && !isEditing) {
                classNames.push(css.readonlyEditableCell);
            }
        }
        if(classNames.length > 0) {
            if(TD.className) {
                classNames.push(TD.className);
            }
            TD.className = classNames.join(" ");
        }
    }
    setupStyling();

    const mouseHandlingElement = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = mouseHandlingElement.current;
        if(!element || !hotRef) {
            return;
        }
        const mouseDownHandler = (e: MouseEvent) => {
            if(e.button !== 2) { // right click
                return;
            }
            if(!hotRef.hotInstance) {
                return;
            }
            hotRef.hotInstance.selectColumns(col);
            (hotRef.hotInstance.getPlugin("contextMenu") as ContextMenu).close();
            (hotRef.hotInstance.getPlugin("contextMenu") as ContextMenu).open(e);
            e.stopPropagation();
            e.preventDefault();
        };
        element.addEventListener("mousedown", mouseDownHandler);

        return () => {
            if(element) {
                element.removeEventListener("mousedown", mouseDownHandler);
            }
            if(hotRef.hotInstance) {
                (hotRef.hotInstance.getPlugin("contextMenu") as ContextMenu).close();
            }
        };
    }, []);


    if(row == 0 && col == 0) {
        return (
            <div className={css.descriptionHeaderCell}>
                <span>{value}</span>
                <Button onClick={handleAllRowsToggleClick} size="small" isBasic>
                    {allRowsExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
                </Button>
            </div>
        );
    }
    if(row > 0 && col == 0 && unitTypeId) {
        return (
            <div className={css.descriptionHeaderCell}>
                <span>{value}</span>
                <Button onClick={() => handleRowToggleClick(row)} size="small" isBasic>
                    {hiddenRows.includes(row + 1) ? <ChevronRightIcon /> : <ChevronDownIcon />}
                </Button>
            </div>
        );
    }
    if(row == 0 && col > 0) {
        return <div
            ref={mouseHandlingElement}
            className={cn(css.monthHeaderCell, invalidForecastedMonthsOverrideState[col - 1] ? css.monthHeaderCellInvalid : undefined)}
            dangerouslySetInnerHTML={{__html: value}}
        />;
    }
    return value;
}
